import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Checkbox from '../../../../../common/checkbox/checkbox';
import CountryDropdown, { CountryDropdownProps } from '../../../../../common/country-dropdown/country-dropdown';
import translation from '../../../../../languages';
import { useAppSelector } from '../../../../../store';
import cn from '../../../../../utils/classnames';
import { COUNTRY_VALIDATION_REG_EXS } from '../../../../../utils/validator';
import { changeFormData, changeState, validateField } from '../../../reducers';
import serializeQueryParams from '../../../../../utils/url/serialize-query-params';

type InvoiceFormPropsType = TranslationProps &
    Pick<CountryDropdownProps, 'disabledCountries'> & {};
const InvoiceForm: FC<InvoiceFormPropsType> = ({ t, ...props }) => {
    const { activePaymentOption } = useSelector((state: RootState) => state.payment);
    const { disabledCountries } = props;
    const { withInvoice, formErrors, formData } = useAppSelector((state) => state.payment);

    const [showCheckbox, setShowCheckbox] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const queryParams = serializeQueryParams();
    const dispatch = useDispatch();

    const chooseCountry = (c: Parameters<CountryDropdownProps['onChange']>[0]) => {
        dispatch(changeFormData({ country: c.alpha2.toUpperCase() }));
    };

    const handleInvoiceCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(changeState({ withInvoice: e.target.checked }));
    };

    const changeInvoiceFormModel = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(changeFormData({ [e.target.name]: e.target.value }));
    };

    useEffect(() => {
        setShowCheckbox(activePaymentOption?.id !== 'Invoice' && !queryParams.ppTransactionId);
        setShowForm(withInvoice || activePaymentOption?.id === 'Invoice');
    }, [withInvoice, activePaymentOption?.id]);

    return (
        <>
            {showCheckbox && (
                <div className='payment-container'>
                    <Checkbox
                        className='lg second-checkbox'
                        id='get-invoice'
                        checked={withInvoice}
                        onChange={handleInvoiceCheckboxChange}
                        title={t('payment.paymentForm.invoiceNeeded')}
                        type='checkbox'
                        checkedBgColor='dark'
                    />
                </div>
            )}
            {showForm && (
                <div className='invoice-form'>
                    <div className='row'>
                        <div className='radio-block col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                            <div>
                                {t('payment.paymentForm.labels.type')} *
                            </div>
                            <div className='radio-offset'>
                                <span>
                                    <input
                                        name='type'
                                        id='person'
                                        value='person'
                                        checked={formData.type === 'person'}
                                        type='radio'
                                        onChange={changeInvoiceFormModel}
                                    />
                                    <label htmlFor='person'>{t('payment.paymentForm.labels.person')}</label>
                                </span>
                                <span className='offset-1'>
                                    <input
                                        name='type'
                                        id='firma'
                                        type='radio'
                                        value='firma'
                                        checked={formData.type === 'firma'}
                                        onChange={changeInvoiceFormModel}
                                    />
                                    <label htmlFor='firma'>{t('payment.paymentForm.labels.firm')}</label>
                                </span>
                            </div>
                        </div>

                        <div className='radio-block col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                            <div>
                                {t('payment.paymentForm.labels.salutation')} *
                            </div>
                            <div className='radio-offset'>
                                <span>
                                    <input
                                        id='female'
                                        type='radio'
                                        name='gender'
                                        value='female'
                                        checked={formData.gender === 'female'}
                                        onChange={changeInvoiceFormModel}
                                    />
                                    <label htmlFor='female'>{t('payment.paymentForm.labels.Mrs')}</label>
                                </span>
                                <span className='offset-1'>
                                    <input
                                        id='male'
                                        type='radio'
                                        name='gender'
                                        value='male'
                                        checked={formData.gender === 'male'}
                                        onChange={changeInvoiceFormModel}
                                    />
                                    <label htmlFor='male'>{t('payment.paymentForm.labels.Mr')}</label>
                                </span>
                            </div>
                        </div>

                        <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                            <span className='lmp-regular'>{t('payment.paymentForm.labels.name')} *</span>
                            <input
                                onBlur={() => dispatch(validateField('firstName'))}
                                value={formData.firstName}
                                onChange={changeInvoiceFormModel}
                                name='firstName'
                                className={cn('form-control', {
                                    'error-field': formErrors.firstName
                                })}
                            />
                        </div>

                        <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                            <span className='lmp-regular'>{t('payment.paymentForm.labels.surname')} *</span>
                            <input
                                name='lastName'
                                onBlur={() => dispatch(validateField('lastName'))}
                                value={formData.lastName}
                                onChange={changeInvoiceFormModel}
                                className={cn('form-control', {
                                    'error-field': formErrors.lastName
                                })}
                            />
                        </div>

                        <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                            <span className='lmp-regular'>{t('payment.paymentForm.labels.street')} *</span>
                            <input
                                onBlur={() => dispatch(validateField('streetAddress'))}
                                name='streetAddress'
                                value={formData.streetAddress}
                                className={cn('form-control', {
                                    'error-field': formErrors.streetAddress
                                })}
                                onChange={changeInvoiceFormModel}
                            />
                        </div>

                        {(formData.type === 'person' || !formData.type) && (
                            <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                                <span className='lmp-regular'>{t('payment.paymentForm.labels.address')}</span>
                                <input
                                    placeholder={t('payment.paymentForm.labels.addressExample')}
                                    className='form-control'
                                    value={formData.streetAddress2}
                                    onChange={changeInvoiceFormModel}
                                    name='streetAddress2'
                                />
                            </div>
                        )}

                        {formData.type === 'firma' && (
                            <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                                <span className='lmp-regular'>{t('payment.paymentForm.labels.company')}</span>
                                <input
                                    className='form-control'
                                    name='company'
                                    value={formData.company}
                                    onChange={changeInvoiceFormModel}
                                />
                            </div>
                        )}

                        <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                            <span className='lmp-regular'>{t('payment.paymentForm.labels.postcode')} *</span>
                            <input
                                name='postcode'
                                onBlur={() => dispatch(validateField('postcode'))}
                                className={cn('form-control', {
                                    'error-field': formErrors.postcode
                                })}
                                value={formData.postcode}
                                onChange={changeInvoiceFormModel}
                                type='text'
                            />
                        </div>

                        <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                            <span className='lmp-regular'>{t('payment.paymentForm.labels.city')} *</span>
                            <input
                                name='city'
                                onBlur={() => dispatch(validateField('city'))}
                                className={cn('form-control', {
                                    'error-field': formErrors.city
                                })}
                                value={formData.city}
                                onChange={changeInvoiceFormModel}
                            />
                        </div>

                        <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                            <span className='lmp-regular'>{t('payment.paymentForm.labels.country')}</span>
                            <CountryDropdown
                                disabledCountries={disabledCountries}
                                value={formData.country}
                                noDefaultCountry={false}
                                onChange={chooseCountry}
                            />
                            <input name='country' type='hidden' value={formData.country} />
                        </div>

                        {formData.type === 'firma' &&
                            Object.keys(COUNTRY_VALIDATION_REG_EXS).includes(formData.country) && (
                                <div className='col-xs-12 col-sm-6 col-md-6 col-lg-6'>
                                    <span className='lmp-regular'>{t('payment.paymentForm.labels.vatId')}</span>
                                    <input
                                        name='vat_id'
                                        onBlur={() => dispatch(validateField('vat_id'))}
                                        className={cn('form-control', {
                                            'error-field': formErrors.vat_id
                                        })}
                                        value={formData.vat_id}
                                        onChange={changeInvoiceFormModel}
                                    />
                                </div>
                            )}
                    </div>
                </div>
            )}
        </>
    );
};

const translate = translation('payment');

export default translate(InvoiceForm);
